// The rules for the workspace's client sync state (symphony-bff GetClientSyncState /
// MarkHomeCategoriesSeen): when each Home category was last opened on ANY device, so the six tiles'
// "new" discs agree across web, iOS and Android. The React half is `useHome3CategoryVisits`.
//
// Kept import-free (types only) so the unit test does not drag the app's dependency graph — see
// orion-web/CLAUDE.md → "Tests (jest)".

import type { Home3Category } from './last-visited';

/** The wire shape of symphony-bff's `SymphonyBff` document, as the route and the duplexer push send it. */
export interface ClientSyncStateWire {
  id?: string;
  homeSeen?: Partial<Record<HomeSeenField, string>>;
}

export type HomeSeenField =
  | 'allSeenDate'
  | 'tasksSeenDate'
  | 'goalsSeenDate'
  | 'routinesSeenDate'
  | 'filesSeenDate'
  | 'appsSeenDate'
  | 'outreachSeenDate';

const FIELD: Record<Home3Category, HomeSeenField> = {
  tasks: 'tasksSeenDate',
  goals: 'goalsSeenDate',
  routines: 'routinesSeenDate',
  files: 'filesSeenDate',
  apps: 'appsSeenDate',
  outreach: 'outreachSeenDate',
};

/** The `HomeCategory` enum names the BFF takes, one per category tile. */
export const HOME_CATEGORY_WIRE: Record<Home3Category, string> = {
  tasks: 'TASKS',
  goals: 'GOALS',
  routines: 'ROUTINES',
  files: 'FILES',
  apps: 'APPS',
  outreach: 'OUTREACH',
};

/** The "All" tile. It has its own time, so opening it never clears the six categories. */
export const HOME_CATEGORY_WIRE_ALL = 'ALL';

export type CategoryStamps = Record<Home3Category, number | null>;

/** The server's time per category, in epoch ms; a missing or unparseable time is "never opened". */
export function serverStamps(state: ClientSyncStateWire | null | undefined): Partial<Record<Home3Category, number>> {
  const out: Partial<Record<Home3Category, number>> = {};
  for (const category of Object.keys(FIELD) as Home3Category[]) {
    const raw = state?.homeSeen?.[FIELD[category]];
    const ms = typeof raw === 'string' ? Date.parse(raw) : NaN;
    if (Number.isFinite(ms)) out[category] = ms;
  }
  return out;
}

/**
 * Each category moved to the later of this browser's stamp and the server's. A time never moves back.
 *
 * A server time is clamped to this browser's `nowMs`: `newSince` reads a stamp from the future as a
 * wrong clock and falls back to the last 24 hours, so a server a few seconds ahead of this browser
 * would bring back the disc the reader had just cleared.
 */
export function mergeStamps(local: CategoryStamps, server: Partial<Record<Home3Category, number>>, nowMs: number): CategoryStamps {
  const out = { ...local };
  for (const category of Object.keys(server) as Home3Category[]) {
    const remote = Math.min(server[category] as number, nowMs);
    const mine = local[category];
    if (mine === null || mine === undefined || remote > mine) out[category] = remote;
  }
  return out;
}

/** This browser's stamps as the BFF's `imported`, for the one-time upload. Null when there is none to send. */
export function importedPayload(local: CategoryStamps): Partial<Record<HomeSeenField, string>> | null {
  const out: Partial<Record<HomeSeenField, string>> = {};
  for (const category of Object.keys(FIELD) as Home3Category[]) {
    const ms = local[category];
    if (ms !== null && ms !== undefined && Number.isFinite(ms)) out[FIELD[category]] = new Date(ms).toISOString();
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** The workspace this browser's stamps belong to. The stamps themselves are per browser, not per account. */
export const STAMPS_OWNER_KEY = 'symphony-home-seen-owner';

const NO_STAMPS: CategoryStamps = { tasks: null, goals: null, routines: null, files: null, apps: null, outreach: null };

export interface Reconciled {
  stamps: CategoryStamps;
  /** Send these as `imported`, then take the answer's id as the owner. */
  importSeen: Partial<Record<HomeSeenField, string>> | null;
  /** The owner to store now; `undefined` leaves it as it is. */
  owner: string | null | undefined;
}

/**
 * Merges the server's state into this browser's stamps, and decides whether to upload them.
 *
 * `owner` is the workspace the stamps were last synced for, or null when this browser never synced. A state
 * whose id differs from it, or that has no id (a workspace that never wrote), is another account's
 * workspace: the stamps here are not its own, so they are dropped and never uploaded, or they would hide
 * that account's badges on every device. An unknown owner is a browser from before the sync existed: its
 * stamps are uploaded once. With nothing to upload, the owner stays unknown, so a stamp made later (a tap
 * while the server was unreachable) is still uploaded on a later load.
 */
export function reconcile(local: CategoryStamps, state: ClientSyncStateWire, owner: string | null, nowMs: number): Reconciled {
  const id = state.id ?? null;
  const server = serverStamps(state);
  if (owner !== null && owner !== id) {
    return { stamps: mergeStamps(NO_STAMPS, server, nowMs), importSeen: null, owner: id };
  }
  const stamps = mergeStamps(local, server, nowMs);
  if (owner !== null) return { stamps, importSeen: null, owner: undefined };
  const upload = importedPayload(local);
  if (upload) return { stamps, importSeen: upload, owner: undefined };
  return { stamps, importSeen: null, owner: id ?? undefined };
}
